//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import Cuid from '@models/Cuid';
import {
  SchedulerMediaError,
  SchedulerMediaValidationError,
} from '@universal-scheduling/components/scheduler/top-level-fields/shared-media/schedulerSharedMediaValidationUtils';
import { getSocialNetworkPostTypeName } from '@universal-scheduling/utils/scheduler/schedulerSocialMediaNetworkUtils';
import { toGrammaticallyCorrectList } from '@utils/stringUtils';

export const SchedulerTwitterMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'X supports up to 4 images or 1 gif/video per post. Change your media selection to post to X.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'X supports up to 4 images or 1 gif/video per post. Change your media selection to post to X.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Posts must include at least 1 image or video. Add media to schedule this post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'X supports up to 4 images or 1 gif/video per post. Change your media selection to post to X.',
  [SchedulerMediaError.MediaDimensionsTooLarge]:
    'X media must have a lower width and height than 8192 pixels',
  [SchedulerMediaError.MediaDimensionsTooSmall]:
    'X media must have a higher width and height than 4 pixels',
};

export const SchedulerInstagramFeedAutomaticMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'Instagram supports up to 10 images and videos per automatic feed post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'Instagram supports up to 10 images and videos per automatic feed post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Instagram Feed posts must include at least 1 image or video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'Instagram supports up to 10 images and videos per automatic feed post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.InvalidImageAspectRatio]:
    'Instagram Feed image posts must have an aspect ratio between 1.19:1 and 4:5',
  [SchedulerMediaError.ImageFileTooLarge]:
    'Instagram Feed images must be smaller than 8MB',
  [SchedulerMediaError.InvalidVideoAspectRatio]:
    'Instagram Feed video posts must have an aspect ratio between 1.91:1 and 9:16',
  [SchedulerMediaError.VideoFileTooLarge]:
    'Instagram Feed videos must be smaller than 100 MB',
  [SchedulerMediaError.VideoTooLong]:
    'Instagram Feed videos must be shorter than 60 seconds',
  [SchedulerMediaError.VideoTooShort]:
    'Instagram Feed videos must be longer than 3 seconds',
};

export const SchedulerInstagramFeedManualMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'Instagram supports up to 10 images and videos per manual feed post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'Instagram supports up to 10 images and videos per manual feed post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Instagram Feed posts must include at least 1 image or video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'Instagram Feed supports up to 10 images and videos per manual feed post. Change your media selection to post to Instagram.',
};

export const SchedulerInstagramReelsAutomaticMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'Instagram supports 1 video per automatic Reels post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'Instagram supports 1 video per automatic Reels post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Instagram Reels posts must include 1 video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'Instagram supports 1 video per automatic Reels post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.InvalidVideoAspectRatio]:
    'Instagram Reels must have an aspect ratio between 0.01:1 and 10:1',
  [SchedulerMediaError.VideoFileTooLarge]:
    'Instagram Reels must be smaller than 1GB',
  [SchedulerMediaError.VideoTooLong]:
    'Instagram Reels must be shorter than 15 minutes',
  [SchedulerMediaError.VideoTooShort]:
    'Instagram Reels must be longer than 3 seconds',
};

export const SchedulerInstagramReelsManualMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'Instagram supports 1 video per manual Reels post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'Instagram supports 1 video per manual Reels post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Instagram Reels posts must include 1 video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'Instagram supports 1 video per manual Reels post. Change your media selection to post to Instagram.',
};

export const SchedulerInstagramStoryAutomaticMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'Instagram supports up to 10 images and videos per automatic Story post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'Instagram supports up to 10 images and videos per automatic Story post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Instagram Story posts must include at least 1 image or video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'Instagram supports up to 10 images and videos per automatic Story post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.ImageFileTooLarge]:
    'Instagram Story images must be smaller than 8MB',
  [SchedulerMediaError.VideoFileTooLarge]:
    'Instagram Story videos must be smaller than 100 MB',
  [SchedulerMediaError.VideoTooLong]:
    'Instagram Story videos must be shorter than 60 seconds',
  [SchedulerMediaError.VideoTooShort]:
    'Instagram Story videos must be longer than 3 seconds',
};

export const SchedulerInstagramStoryManualMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'Instagram supports up to 10 images and videos per Story post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'Instagram supports up to 10 images and videos per Story post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Instagram Story posts must include at least 1 image or video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'Instagram supports up to 10 images and videos per Story post. Change your media selection to post to Instagram.',
  [SchedulerMediaError.ImageFileTooLarge]:
    'Instagram Story images must be smaller than 8MB',
  [SchedulerMediaError.VideoFileTooLarge]:
    'Instagram Story videos must be smaller than 100 MB',
  [SchedulerMediaError.VideoTooLong]:
    'Instagram Story videos must be shorter than 60 seconds',
  [SchedulerMediaError.VideoTooShort]:
    'Instagram Story videos must be longer than 3 seconds',
};

export const SchedulerFacebookMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'Facebook supports multiple images or 1 video per feed post. Change your media selection to post to Facebook.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'Facebook supports multiple images or 1 video per feed post. Change your media selection to post to Facebook.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Facebook posts must include at least 1 image or video. Add media to schedule this post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'Facebook supports multiple images or 1 video per feed post. Change your media selection to post to Facebook.',
  [SchedulerMediaError.ImageFileTooLarge]:
    'Facebook images must be smaller than 10MB',
  [SchedulerMediaError.VideoTooLong]:
    'Facebook videos must be shorter than 40 minutes',
} as const;

export const SchedulerPinterestMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'Pinterest supports up to 5 images or 1 gif/video per pin. Change your media selection to post to Pinterest.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'Pinterest supports up to 5 images or 1 gif/video per pin. Change your media selection to post to Pinterest.',
  [SchedulerMediaError.TooFewMediaItems]:
    'Pinterest pins must include at least 1 image or gif/video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'Pinterest supports up to 5 images or 1 video per pin. Change your media selection to post to Pinterest.',
  [SchedulerMediaError.MediaDimensionsTooSmall]:
    'Pinterest media must have a higher width than 200 pixels and higher height than 300 pixels',
  [SchedulerMediaError.VideoTooLong]:
    'Pinterest videos must be shorter than 15 minutes',
};

export const SchedulerTikTokMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'TikTok supports 1 video per post. Change your media selection to post to TikTok.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'TikTok supports 1 video per post. Change your media selection to post to TikTok.',
  [SchedulerMediaError.TooFewMediaItems]:
    'TikTok posts must include 1 video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'TikTok supports 1 video per post. Change your media selection to post to TikTok.',
  [SchedulerMediaError.MediaDimensionsTooSmall]:
    'TikTok videos must have a higher width and height than 360 pixels',
  [SchedulerMediaError.VideoTooLong]:
    'TikTok videos must be shorter than 10 minutes',
  [SchedulerMediaError.VideoTooShort]:
    'TikTok videos must be longer than 3 seconds',
};

export const YOUTUBE_MAX_VIDEO_DURATION_MILLIS = 12 * 60 * 60 * 1000;
export const YOUTUBE_MAX_VIDEO_SIZE_BYTES = 256 * 1024 * 1024 * 1024;

export const SchedulerYouTubeMediaError: Partial<
  Record<SchedulerMediaError, string>
> = {
  [SchedulerMediaError.InvalidMediaType]:
    'YouTube supports 1 video per post. Change your media selection to post to YouTube.',
  [SchedulerMediaError.InvalidMixedMediaTypes]:
    'YouTube supports 1 video per post. Change your media selection to post to YouTube.',
  [SchedulerMediaError.TooFewMediaItems]:
    'YouTube posts must include 1 video to post.',
  [SchedulerMediaError.TooManyMediaItems]:
    'YouTube supports 1 video per post. Change your media selection to post to YouTube.',
  [SchedulerMediaError.VideoFileTooLarge]:
    'YouTube videos must be smaller than 256 GB',
  [SchedulerMediaError.VideoTooLong]:
    'YouTube videos must be shorter than 12 hours',
};

export const SCHEDULER_FORM_ERROR_MESSAGE =
  "Resolve this post's errors before scheduling.";

export const getSchedulerMediaErrorMessage = (
  error: SchedulerMediaValidationError,
  isForSingleMediaThumbnail?: boolean,
): string => {
  const specificErrorMessage = (() => {
    switch (error.postType) {
      case UniversalPostType.FacebookPageFeedPost:
        return SchedulerFacebookMediaError[error.error];
      case UniversalPostType.InstagramFeedAutomaticPost:
        return SchedulerInstagramFeedAutomaticMediaError[error.error];
      case UniversalPostType.InstagramFeedManualPost:
        return SchedulerInstagramFeedManualMediaError[error.error];
      case UniversalPostType.InstagramReelsAutomaticPost:
        return SchedulerInstagramReelsAutomaticMediaError[error.error];
      case UniversalPostType.InstagramReelsManualPost:
        return SchedulerInstagramReelsManualMediaError[error.error];
      case UniversalPostType.InstagramStoryGroupAutomaticPost:
        return SchedulerInstagramStoryAutomaticMediaError[error.error];
      case UniversalPostType.InstagramStoryGroupPost:
        return SchedulerInstagramStoryManualMediaError[error.error];
      case UniversalPostType.PinterestOrganicPin:
        return SchedulerPinterestMediaError[error.error];
      case UniversalPostType.TikTokVideoAutomaticPost:
        return SchedulerTikTokMediaError[error.error];
      case UniversalPostType.TwitterMediaPost:
        return SchedulerTwitterMediaError[error.error];
      case UniversalPostType.YouTubeAutomaticPost:
        return SchedulerYouTubeMediaError[error.error];
    }
  })();

  return specificErrorMessage
    ? `${specificErrorMessage}${isForSingleMediaThumbnail ? '.' : ''}`
    : `${getSocialNetworkPostTypeName(error.postType)} does not support your media`; // Fallback shouldn't happen since all valid errors have messages
};

export const getSchedulerMediaErrorMessageWithPositions = (
  error: SchedulerMediaValidationError,
  selectedCuids: Cuid[],
) => {
  const errorMessage = getSchedulerMediaErrorMessage(error);

  const errorMediaPositions = selectedCuids
    .reduce<number[]>((errorPositionsAccumulator, cuid, index) => {
      return error.cuids.includes(cuid)
        ? [...errorPositionsAccumulator, index + 1]
        : errorPositionsAccumulator;
    }, [])
    .filter((position) => position !== 0);
  const erroredMediaText = toGrammaticallyCorrectList(
    errorMediaPositions.map((position, idx) => {
      return `Media ${position}${idx === errorMediaPositions.length - 1 ? '.' : ' '}`;
    }),
  );

  return `${errorMessage}${selectedCuids.length > 1 ? ` for ${erroredMediaText}` : '.'}`;
};
